/**
 * Org-level sharing settings a plugin can register.
 *
 * Some surfaces hand out access to a session on their own — the Slack
 * plugin sharing a channel-started session with everyone who replies in
 * its thread is the first. Whether that is allowed is not the surface's
 * decision: it is an org policy, it belongs next to the two session-sharing
 * levels on the org Sharing page, and it can never exceed them.
 *
 * Rather than growing a bespoke column, contract method, and settings
 * switch per plugin, a plugin declares one {@link SessionSharingSetting}
 * and the host does the rest:
 *
 *   - the declaration goes on `PluginServerDefinition.sessionSharingSettings`
 *     (so the host knows the key exists and can validate writes) and on the
 *     plugin's web manifest (so the org Sharing page renders it);
 *   - the value lives in one host-owned `organizations.sharing_settings`
 *     map, written through `PATCH /organizations/:id` behind `org:manage`;
 *   - the plugin reads it back pre-clamped through
 *     `OrganizationServiceContract.isSessionSharingSettingEnabled`.
 *
 * The clamp is the reason this is a host concept rather than a plugin
 * setting. A setting declares which {@link ResourceGrantAccess} level the
 * access it hands out amounts to, and the org's own
 * `SessionSharingPolicy` is the ceiling over it: an org that forbids
 * `edit` shares cannot have one opened by a chat surface, whatever the
 * surface asked for and whatever the org opted into earlier. Every reader
 * goes through {@link sessionSharingSettingEnabled}, so "did you check the
 * ceiling too?" is not a question a call site can get wrong.
 *
 * @module @reflex/shared/session-sharing-settings
 */

import type { ResourceGrantAccess } from './resource-grant.types.js';
import {
  SESSION_SHARING_SETTING_KEY_PATTERN,
  type SessionSharingSettings,
} from './session-sharing-settings.types.js';
import type { DefaultSessionShare } from './default-session-share.js';
import {
  defaultSessionShareAccess,
  defaultSessionShareCovers,
  sessionSharingPolicy,
} from './session-sharing.js';

declare const sessionSharingSettingKeyBrand: unique symbol;

/**
 * A registered setting's key, `<plugin>:<name>` (e.g.
 * `slack:channel-participants`). Branded so only
 * {@link defineSessionSharingSetting} can mint one — a free-form string
 * cannot be passed where a declared setting's key is expected.
 */
export type SessionSharingSettingKey = string & {
  readonly [sessionSharingSettingKeyBrand]: 'SessionSharingSettingKey';
};

/**
 * What the org Sharing page renders for the setting. The host owns the
 * layout and the clamped/unavailable state; the plugin owns the words,
 * because only it knows what its surface does.
 *
 * Copy follows `docs/internal/design/writing-style.md`: say what happens,
 * not what the switch is called.
 */
export interface SessionSharingSettingCopy {
  /** Section heading, e.g. "Sessions started from a chat channel". */
  title: string;
  /** What the setting is for, above the switch. One or two sentences. */
  description: string;
  /** Label beside the switch, e.g. "Share with everyone in the thread". */
  switchLabel: string;
  /** Summary line shown while the setting is on. */
  whenOn: string;
  /** Summary line shown while the setting is off. */
  whenOff: string;
  /**
   * Summary line shown while the org's sharing ceiling forbids the level
   * this setting hands out, so the switch is off and unusable whatever the
   * org stored. Defaults to a generic sentence naming the level.
   */
  whenUnavailable?: string;
  /**
   * Always-visible line under the summary, for the consequence an owner
   * should read before turning it on (e.g. whose credentials the shared
   * sandbox holds). Optional.
   */
  disclosure?: string;
}

/**
 * One org-level sharing setting, declared by the plugin that acts on it.
 * Mint with {@link defineSessionSharingSetting}.
 */
export interface SessionSharingSetting {
  readonly key: SessionSharingSettingKey;
  /**
   * The access level the sharing this setting unlocks confers. The org's
   * matching `SessionSharingPolicy` level is the ceiling over the
   * setting: with that level off org-wide, the setting reads `false` no
   * matter what the org stored.
   */
  readonly grants: ResourceGrantAccess;
  /**
   * The answer for an org that has never touched the setting. Prefer
   * `false` for anything that widens who can reach someone else's work:
   * an org that never opts in should behave as it did before the setting
   * existed.
   */
  readonly defaultEnabled: boolean;
  readonly copy: SessionSharingSettingCopy;
  /** Display order on the Sharing page, ascending. Defaults to 0. */
  readonly order: number;
}

export interface SessionSharingSettingInput extends Omit<SessionSharingSetting, 'key' | 'order'> {
  /** `<plugin>:<name>`, e.g. `slack:channel-participants`. */
  key: string;
  order?: number;
}

/**
 * Declare a sharing setting. Validates the key's shape at module load, so
 * a malformed key fails the import rather than storing a value nothing
 * will ever read back.
 */
export function defineSessionSharingSetting(
  input: SessionSharingSettingInput,
): SessionSharingSetting {
  const { key, grants, defaultEnabled, copy, order = 0 } = input;
  if (!SESSION_SHARING_SETTING_KEY_PATTERN.test(key)) {
    throw new TypeError(
      `Session sharing setting key '${key}' must be '<plugin>:<name>' in kebab-case ` +
        `(e.g. 'slack:channel-participants').`,
    );
  }
  return {
    key: key as SessionSharingSettingKey,
    grants,
    defaultEnabled,
    copy,
    order,
  };
}

/** The plugin half of a setting's key — what the boot assertion checks against. */
export function sessionSharingSettingNamespace(setting: SessionSharingSetting): string {
  return setting.key.split(':')[0] ?? '';
}

/** The org fields a sharing setting is resolved from. */
export interface SessionSharingSettingsSource {
  allowSessionShareView?: boolean;
  allowSessionShareEdit?: boolean;
  sharingSettings?: SessionSharingSettings | null;
}

/**
 * What the org stored for the setting, ignoring the ceiling — the
 * launcher-intent value the settings switch is bound to, and never the
 * value a surface acts on. Use {@link sessionSharingSettingEnabled} to
 * decide whether the sharing may actually happen.
 */
export function sessionSharingSettingStored(
  setting: SessionSharingSetting,
  org: SessionSharingSettingsSource | null | undefined,
): boolean {
  return org?.sharingSettings?.[setting.key] ?? setting.defaultEnabled;
}

/**
 * Whether the org's sharing ceiling leaves room for this setting at all.
 * `false` makes the switch read off and unusable: an org that forbids the
 * level the setting hands out cannot be opted into it.
 */
export function sessionSharingSettingAvailable(
  setting: SessionSharingSetting,
  org: SessionSharingSettingsSource | null | undefined,
): boolean {
  return sessionSharingPolicy(org)[setting.grants];
}

/**
 * The one reader every surface uses: the org's stored choice resolved
 * against the ceiling above it. An absent org reads `false` — a setting we
 * could not resolve must never read as one that was turned on.
 */
export function sessionSharingSettingEnabled(
  setting: SessionSharingSetting,
  org: SessionSharingSettingsSource | null | undefined,
): boolean {
  if (!org) return false;
  return sessionSharingSettingStored(setting, org) && sessionSharingSettingAvailable(setting, org);
}

/** Human name of a share level, for the generic clamped copy. */
const ACCESS_LABEL: Readonly<Record<ResourceGrantAccess, string>> = {
  view: 'view access',
  edit: 'edit access',
};

/**
 * Why the setting is unusable right now, shown in place of the summary
 * line while the ceiling forbids it. The plugin may override the wording
 * with {@link SessionSharingSettingCopy.whenUnavailable}.
 */
export function sessionSharingSettingUnavailableCopy(setting: SessionSharingSetting): string {
  return (
    setting.copy.whenUnavailable ??
    `Unavailable while sharing with ${ACCESS_LABEL[setting.grants]} is off. This setting hands ` +
      `out ${ACCESS_LABEL[setting.grants]}, so it cannot exceed the limit above.`
  );
}

/**
 * Whether the org's default already hands every member what this setting
 * would hand out, making it redundant for sessions created from now on.
 *
 * Deliberately NOT folded into {@link sessionSharingSettingEnabled}, and
 * this is the important part: unlike the ceiling, which forbids the share
 * outright, the default merely subsumes it — and only for sessions created
 * while the default was in force. The default is read once, when a session
 * is created, so a session that predates it has no org-wide grant and
 * still needs this setting to open one. Skipping the share on the strength
 * of a default that never applied to that session would be a silent access
 * regression, so this stays a settings-page affordance: it explains that
 * the switch no longer changes anything for new sessions, and leaves what
 * the server does alone.
 */
export function sessionSharingSettingCovered(
  setting: SessionSharingSetting,
  defaultShare: DefaultSessionShare,
): boolean {
  return defaultSessionShareCovers(defaultShare, setting.grants);
}

/**
 * Why the setting is redundant, shown while the org default subsumes it.
 *
 * `coveringAccess` is what the ORG DEFAULT hands out, which is not
 * necessarily what this setting would have handed out — an `edit` default
 * covers a `view` setting. Naming the setting's own level here would tell
 * the owner new sessions start at view access when they in fact start at
 * edit, so the two levels are reported separately.
 */
export function sessionSharingSettingCoveredCopy(
  setting: SessionSharingSetting,
  coveringAccess: ResourceGrantAccess,
): string {
  return (
    `New sessions already start shared with everyone in the organization at ` +
    `${ACCESS_LABEL[coveringAccess]}, so this setting's ${ACCESS_LABEL[setting.grants]} no ` +
    `longer changes who can reach them. It still applies to sessions created before that ` +
    `default was set.`
  );
}

/**
 * The summary line for the setting's current state — unavailable first,
 * because the ceiling outranks the stored choice, then covered, because a
 * default that already grants the level outranks the switch without
 * forbidding it.
 *
 * Takes the org default rather than a pre-computed `covered` flag so that
 * whether the setting is covered and what is covering it can never disagree:
 * the caller that renders the switch and the copy that explains it are
 * reading one value, not two that have to be kept in step.
 */
export function sessionSharingSettingSummary(
  setting: SessionSharingSetting,
  state: { stored: boolean; available: boolean; defaultShare: DefaultSessionShare },
): string {
  if (!state.available) return sessionSharingSettingUnavailableCopy(setting);
  const coveringAccess = defaultSessionShareAccess(state.defaultShare);
  if (coveringAccess && sessionSharingSettingCovered(setting, state.defaultShare)) {
    return sessionSharingSettingCoveredCopy(setting, coveringAccess);
  }
  return state.stored ? setting.copy.whenOn : setting.copy.whenOff;
}

/**
 * The keys in `patch` that no declaration claims. A caller error rather
 * than a no-op: silently dropping them would store nothing while the page
 * that sent them reports a save, and keeping them would let a typo
 * accumulate in a column only declared keys are ever read back from.
 */
export function unknownSessionSharingSettingKeys(
  patch: SessionSharingSettings,
  declared: readonly SessionSharingSetting[],
): string[] {
  const known = new Set<string>(declared.map((setting) => setting.key));
  return Object.keys(patch).filter((key) => !known.has(key));
}
